import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import DictationInput from '../../components/DictationInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  Trophy, CheckCircle, Circle, ChevronRight, User, Clock,
  FileText, Briefcase, Heart, Home, GraduationCap, Award,
  Users, ArrowLeft
} from 'lucide-react';

const MILESTONE_ICONS = {
  intake_completed: FileText,
  orientation_completed: Users,
  house_rules_signed: Home,
  financial_literacy: GraduationCap,
  employment_readiness: Briefcase,
  recovery_support: Heart,
  lease_readiness: Home,
  program_completion: Award,
};

const CATEGORY_COLORS = {
  onboarding: 'bg-blue-100 text-blue-700',
  education: 'bg-purple-100 text-purple-700',
  recovery: 'bg-pink-100 text-pink-700',
  transition: 'bg-amber-100 text-amber-700',
  completion: 'bg-emerald-100 text-emerald-700',
};

export default function MilestonesPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [residents, setResidents] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedResident, setSelectedResident] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [housesRes, pendingRes, statsRes] = await Promise.all([
        api.get('/housing/entity/our_square/houses'),
        api.get('/milestones/pending-approvals?entity=our_square'),
        api.get('/milestones/stats?entity=our_square'),
      ]);
      // Extract all residents from houses
      const allResidents = housesRes.data.flatMap(h => 
        (h.residents || []).map(r => ({ ...r, house_name: h.name }))
      );
      setResidents(allResidents);
      setPendingApprovals(pendingRes.data);
      setStats(statsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  if (selectedResident) {
    return <ResidentMilestones residentId={selectedResident.id} residentName={selectedResident.user_name} api={api} onBack={() => { setSelectedResident(null); load(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="milestones-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Program Milestones</h1>
        <p className="text-sm text-muted-foreground mt-1">Track resident progress through the housing program</p>
      </div>

      {/* Overview Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-cyan-50 text-cyan-700 flex items-center justify-center mb-2"><Users size={18} /></div>
            <p className="text-2xl font-bold text-slate-900">{stats.total_residents || 0}</p>
            <p className="text-xs text-muted-foreground">Active Residents</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center mb-2"><Clock size={18} /></div>
            <p className="text-2xl font-bold text-slate-900">{pendingApprovals.length}</p>
            <p className="text-xs text-muted-foreground">Pending Approvals</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center mb-2"><CheckCircle size={18} /></div>
            <p className="text-2xl font-bold text-slate-900">{stats.milestone_stats?.program_completion?.count || 0}</p>
            <p className="text-xs text-muted-foreground">Program Graduates</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-purple-50 text-purple-700 flex items-center justify-center mb-2"><Trophy size={18} /></div>
            <p className="text-2xl font-bold text-slate-900">
              {stats.milestone_stats?.orientation_completed?.completion_rate || 0}%
            </p>
            <p className="text-xs text-muted-foreground">Orientation Rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Pending Approvals */}
      {pendingApprovals.length > 0 && (
        <Card className="border-0 shadow-sm rounded-xl border-l-4 border-l-amber-400">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock size={16} className="text-amber-600" /> Pending Milestone Approvals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingApprovals.slice(0, 5).map(p => {
                const Icon = MILESTONE_ICONS[p.next_milestone?.key] || Trophy;
                return (
                  <div key={p.resident_id} className="flex items-center justify-between p-3 rounded-lg bg-amber-50 hover:bg-amber-100 cursor-pointer transition-colors" onClick={() => setSelectedResident({ id: p.resident_id, user_name: p.resident_name })}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center text-amber-700 text-sm font-bold">
                        {p.resident_name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{p.resident_name}</p>
                        <p className="text-xs text-amber-700">Next: {p.next_milestone?.name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{p.completed_count}/{p.total_count}</Badge>
                      <Icon size={16} className="text-amber-600" />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Milestone Completion Rates */}
      {stats.milestone_stats && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Milestone Completion Rates</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.milestone_stats).map(([key, data]) => {
                const Icon = MILESTONE_ICONS[key] || Trophy;
                return (
                  <div key={key} className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Icon size={16} className="text-slate-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{data.name}</span>
                        <span className="text-xs text-muted-foreground">{data.count} / {stats.total_residents || 0}</span>
                      </div>
                      <Progress value={data.completion_rate} className="h-2" />
                    </div>
                    <span className="text-sm font-bold w-12 text-right">{data.completion_rate}%</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Residents */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">All Residents</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {residents.map(r => (
              <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors" onClick={() => setSelectedResident(r)} data-testid={`resident-${r.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 text-sm font-bold">
                    {r.user_name?.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{r.user_name}</p>
                    <p className="text-xs text-muted-foreground">{r.house_name} • {r.program_phase}</p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-muted-foreground" />
              </div>
            ))}
            {residents.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No active residents</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ResidentMilestones({ residentId, residentName, api, onBack }) {
  const [data, setData] = useState(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [approvalForm, setApprovalForm] = useState({ notes: '', evidence: '' });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/milestones/resident/${residentId}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, residentId]);

  useEffect(() => { load(); }, [load]);

  const approveMilestone = async () => {
    if (!selectedMilestone) return;
    try {
      await api.post(`/milestones/resident/${residentId}/approve`, {
        milestone_key: selectedMilestone.key,
        notes: approvalForm.notes,
        evidence: approvalForm.evidence,
      });
      toast.success('Milestone approved!');
      setApproveOpen(false);
      setSelectedMilestone(null);
      setApprovalForm({ notes: '', evidence: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openApproval = (milestone) => {
    setSelectedMilestone(milestone);
    setApprovalForm({ notes: '', evidence: '' });
    setApproveOpen(true);
  };

  if (loading || !data) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="resident-milestones">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack} className="rounded-full"><ArrowLeft size={16} /></Button>
          <div>
            <h2 className="text-xl font-bold text-slate-900">{data.resident_name}</h2>
            <p className="text-xs text-muted-foreground">Program Progress</p>
          </div>
        </div>
      </div>

      {/* Progress Overview */}
      <Card className="border-0 shadow-sm rounded-xl bg-gradient-to-r from-cyan-50 to-teal-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-3xl font-bold text-cyan-700">{data.progress_percent}%</p>
              <p className="text-sm text-muted-foreground">Program Complete</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-900">{data.completed_count}/{data.total_count}</p>
              <p className="text-sm text-muted-foreground">Milestones Achieved</p>
            </div>
          </div>
          <Progress value={data.progress_percent} className="h-3" />
        </CardContent>
      </Card>

      {/* Milestones List */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Program Milestones</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data.milestones.map((m, idx) => {
              const Icon = MILESTONE_ICONS[m.key] || Trophy;
              const isCompleted = m.completed;
              const isNext = !isCompleted && idx === data.milestones.findIndex(ms => !ms.completed);
              
              return (
                <div key={m.key} className={`flex items-start gap-4 p-4 rounded-xl transition-colors ${isCompleted ? 'bg-emerald-50' : isNext ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isCompleted ? 'bg-emerald-500 text-white' : isNext ? 'bg-amber-400 text-white' : 'bg-slate-200 text-slate-500'}`}>
                    {isCompleted ? <CheckCircle size={20} /> : <Icon size={20} />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`font-medium ${isCompleted ? 'text-emerald-700' : 'text-slate-900'}`}>{m.name}</p>
                      <Badge className={`${CATEGORY_COLORS[m.category]} text-xs`}>{m.category}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{m.description}</p>
                    {isCompleted && m.completion_data && (
                      <div className="mt-2 text-xs text-emerald-600">
                        <p>Approved by {m.completion_data.approved_by_name} on {new Date(m.completion_data.approved_at).toLocaleDateString()}</p>
                        {m.completion_data.notes && <p className="mt-1 italic">"{m.completion_data.notes}"</p>}
                      </div>
                    )}
                  </div>
                  {!isCompleted && (
                    <Button size="sm" onClick={() => openApproval(m)} className={`rounded-full ${isNext ? 'bg-amber-500 hover:bg-amber-600' : 'bg-slate-400 hover:bg-slate-500'} text-white`}>
                      Approve
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Approval Dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Approve Milestone</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="p-3 bg-cyan-50 rounded-lg">
              <p className="font-medium text-cyan-800">{selectedMilestone?.name}</p>
              <p className="text-xs text-cyan-600 mt-1">{selectedMilestone?.description}</p>
            </div>
            <div className="space-y-1.5">
              <Label>Staff Notes</Label>
              <DictationInput value={approvalForm.notes} onChange={e => setApprovalForm({ ...approvalForm, notes: e.target.value })} placeholder="Document how the resident met this milestone..." rows={3} showCleanup={false} />
            </div>
            <div className="space-y-1.5">
              <Label>Evidence/Documentation (optional)</Label>
              <Input value={approvalForm.evidence} onChange={e => setApprovalForm({ ...approvalForm, evidence: e.target.value })} placeholder="Link or reference to supporting documentation" />
            </div>
            <Button onClick={approveMilestone} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full">
              Confirm Approval
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
